import { useEffect, useId, useRef, useState } from 'react';
import { t } from '../i18n';
import './FilterBar.css';

/**
 * The drink-by years offered: from 10 years back (a wine already past its date can still be
 * entered) to 50 years on.
 */
export const DRINK_BY_YEARS_BACK = 10;
export const DRINK_BY_YEARS_AHEAD = 50;

/**
 * A year chosen in a list (owner request, 2026-10-06: "À boire avant (facultatif)"): a button
 * showing the year or "—"; the list goes from `from` to `to`, "Aucune" first to clear it, and
 * opens on the chosen year, else on `suggested` (10 years from now). Closes on a tap
 * outside or Escape. Without `label`, a `<label for={buttonId}>` names it.
 */
export function YearPicker({
  buttonId,
  value,
  onChange,
  from,
  to,
  suggested,
}: {
  buttonId: string;
  /** The year, or '' for none. */
  value: string;
  onChange: (year: string) => void;
  /** The first and last years offered. */
  from: number;
  to: number;
  /** Where the list opens when no year is chosen. */
  suggested: number;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const listId = useId();
  const years = Array.from({ length: to - from + 1 }, (_, i) => from + i);
  const focus = value !== '' ? Number(value) : suggested;

  useEffect(() => {
    if (!open) return;
    root.current
      ?.querySelector<HTMLButtonElement>(`[data-year="${focus}"]`)
      ?.scrollIntoView({ block: 'center' });
    const close = (event: Event) => {
      if (event instanceof KeyboardEvent && event.key !== 'Escape') return;
      if (event instanceof PointerEvent && root.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open, focus]);

  const pick = (year: string) => {
    onChange(year);
    setOpen(false);
  };

  return (
    <div className="year-picker multi-select" ref={root}>
      <button
        type="button"
        id={buttonId}
        className="multi-select__button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen(!open)}
      >
        <span className="multi-select__value">{value !== '' ? value : '—'}</span>
      </button>
      {open && (
        <div id={listId} className="multi-select__list year-picker__list" role="group">
          <button
            type="button"
            className="year-picker__year"
            aria-pressed={value === ''}
            onClick={() => pick('')}
          >
            {t('drinkUntil.none')}
          </button>
          {years.map((year) => (
            <button
              key={year}
              type="button"
              data-year={year}
              className={`year-picker__year${year === focus ? ' year-picker__year--focus' : ''}`}
              aria-pressed={String(year) === value}
              onClick={() => pick(String(year))}
            >
              {year}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
